import { useState, useEffect, useCallback } from "react";
import { FileSpreadsheet, FileText, Trash2 } from "lucide-react";
import { api, errMsg, API } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { fmtIDR, fmtNum, todayStr } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const MONEY_KEYS = new Set(["Subtotal", "Discount", "Tax", "Total", "HPP", "Profit", "Avg Cost", "Stock Value", "HPP/Serving", "Suggested Price", "Unit Price", "Cost", "Amount", "Total Cost"]);

export default function Reports() {
  const { t } = useLang();
  const [type, setType] = useState("sales");
  const [filters, setFilters] = useState({ start: todayStr(-30), end: todayStr(), outlet_id: "", product_id: "", category_id: "", cashier_id: "" });
  const [options, setOptions] = useState({ outlets: [], products: [], categories: [], cashiers: [] });
  const [report, setReport] = useState({ headers: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const canDelete = isAdmin && (type === "sales" || type === "purchases");

  const deleteRow = async (row) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(type === "sales" ? `/pos/transactions/${row.id}` : `/purchases/${row.id}`);
      toast.success("OK");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { api.get("/reports/filters").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const res = await api.get("/reports/data", { params });
      setReport(res.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [type, filters]);

  useEffect(() => { load(); }, [load]);

  const exportUrl = (format) => {
    const params = new URLSearchParams({ type, format });
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    return `${API}/reports/export?${params.toString()}`;
  };

  const fmtCell = (h, v) => {
    if (MONEY_KEYS.has(h)) return <span className="font-mono">{fmtIDR(v)}</span>;
    if (typeof v === "number") return <span className="font-mono">{fmtNum(v)}</span>;
    return v;
  };

  const reportTypes = ["sales", "purchases", "inventory", "hpp", "profit-loss", "recipe-cost"];
  const labels = { sales: t("r_sales"), purchases: t("r_purchases"), inventory: t("r_inventory"), hpp: t("r_hpp"), "profit-loss": t("r_pl"), "recipe-cost": t("r_recipe") };

  return (
    <div data-testid="reports-page" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_reports")}</h1>
        <a href={exportUrl("excel")} data-testid="export-excel-btn" className="inline-flex items-center gap-1.5 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors">
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </a>
        <a href={exportUrl("pdf")} data-testid="export-pdf-btn" className="inline-flex items-center gap-1.5 text-sm font-medium bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg transition-colors">
          <FileText className="w-4 h-4" /> PDF
        </a>
      </div>

      <Tabs value={type} onValueChange={setType}>
        <TabsList data-testid="report-type-tabs" className="flex-wrap h-auto">
          {reportTypes.map((rt) => <TabsTrigger key={rt} value={rt} data-testid={`report-tab-${rt}`}>{labels[rt]}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t("startDate")}</div>
          <input data-testid="filter-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="text-sm bg-background border border-border rounded-lg px-3 py-1.5" /></div>
        <div><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t("endDate")}</div>
          <input data-testid="filter-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="text-sm bg-background border border-border rounded-lg px-3 py-1.5" /></div>
        {[
          { key: "outlet_id", label: t("outlet"), items: options.outlets },
          { key: "product_id", label: t("product"), items: options.products },
          { key: "category_id", label: t("category"), items: options.categories },
          { key: "cashier_id", label: t("cashier"), items: options.cashiers },
        ].map((f) => (
          <div key={f.key}>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{f.label}</div>
            <select data-testid={`filter-${f.key}`} value={filters[f.key]} onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })} className="text-sm bg-background border border-border rounded-lg px-3 py-1.5 min-w-[130px]">
              <option value="">{t("all")}</option>
              {f.items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
        ))}
        <Button data-testid="filter-apply-btn" onClick={load} className="bg-amber-600 hover:bg-amber-700 text-white">{t("apply")}</Button>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {report.headers.map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>)}
                {canDelete && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={report.headers.length} className="px-4 py-10 text-center text-muted-foreground">{t("loading")}</td></tr>
              ) : report.rows.length === 0 ? (
                <tr><td colSpan={report.headers.length} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>
              ) : report.rows.map((row, i) => (
                <tr key={`${row[report.headers[0]] ?? "row"}-${i}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  {report.headers.map((h) => <td key={h} className="px-4 py-2.5 whitespace-nowrap">{fmtCell(h, row[h])}</td>)}
                  {canDelete && (
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`report-delete-${row.id}`} onClick={() => deleteRow(row)} className="p-1.5 rounded-md text-muted-foreground hover:text-rose-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
